'use client'

// "View as seeker" — dashboard modal showing a listing exactly as it renders
// in search (the real ListingCard) and on the detail page (the composer's
// PreviewPanel, fed the publish-time draft snapshot). User request 2026-07-15.
import * as React from 'react'
import { LayoutGrid, ReceiptText } from 'lucide-react'
import { RModal, Seg } from '@/components/roostr'
import { ListingCard } from '@/components/listings'
import { useI18n } from '@/lib/i18n'
import { draftToListing, PREVIEW_STATS, PreviewPanel } from './preview-panel'
import type { MyListing, PostDraft } from './post-data'
import type { Listing } from '@/lib/types'

// Seed listings predate the draft snapshot — synthesize one from the fields
// they do carry so the modal never renders empty.
function fallbackDraft(l: MyListing): PostDraft {
  return {
    transaction: l.transaction ?? 'rent',
    photos: Array.from({ length: Math.max(1, l.images) }, (_, i) => ({
      id: `pv${i}`,
      room: '',
      palette: (l.palette + i) % 10,
    })),
    facts: {
      rooms: l.rooms,
      area: l.area,
      floor: l.floor,
      totalFloors: l.totalFloors,
      elevator: true,
      features: l.features ?? [],
    },
    address: { street: '', district: l.district, city: 'Praha', lat: 0, lon: 0 },
    price: l.price,
    deposit: l.deposit ?? 0,
    utilities: l.utilities ?? 0,
    extras: l.extras ?? [],
    tone: null,
    title: l.title,
    body: null,
    published: true,
    contact: { email: '', phone: '', telegram: '', instagram: '', schedule: null },
  }
}

function toListing(l: MyListing, draft: PostDraft): Listing {
  const base = draftToListing(draft)
  return { ...base, id: l.id, title: l.title, district: l.district, price: l.price, updates: l.updates }
}

export function ListingPreviewModal({ l, open, onClose }: { l: MyListing; open: boolean; onClose: () => void }) {
  const { t } = useI18n()
  const [tab, setTab] = React.useState<'card' | 'detail'>('card')
  const draft = l.draft ?? fallbackDraft(l)
  return (
    <RModal open={open} onClose={onClose} width={480} mobileSheet ariaLabel={t('myListings.viewTitle', 'How seekers see it')}>
      <div className="mb-3.5 flex flex-col gap-2">
        <div className="text-[16.5px] font-bold tracking-[-0.2px]">
          {t('myListings.viewTitle', 'How seekers see it')}
        </div>
        <Seg
          value={tab}
          onChange={setTab}
          options={[
            { value: 'card', label: t('myListings.viewInSearch', 'In search'), icon: <LayoutGrid size={16} /> },
            { value: 'detail', label: t('myListings.viewDetail', 'Detail page'), icon: <ReceiptText size={16} /> },
          ]}
        />
      </div>
      <div className="max-h-[64vh] overflow-y-auto pr-1">
        {tab === 'card' ? (
          <div className="pointer-events-none">
            <ListingCard listing={toListing(l, draft)} stats={PREVIEW_STATS} />
          </div>
        ) : (
          <PreviewPanel draft={draft} quality={100} />
        )}
      </div>
    </RModal>
  )
}
